import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import FilledCalendarIcon from 'assets/calendar';
import { useTranslation } from 'react-i18next';
import { DayPicker } from 'components/molecules/DayPicker';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { formatEventMonthDay } from 'utils/dateTime';
import { BottomDrawer } from './BottomDrawer';
import { SelectTriggerButton } from './SelectTriggerButton';

interface BottomSheetDatePickerProps {
  availableDates: string[];
  onMonthChange: (date: moment.Moment) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  value: string;
  onChange: (date: Date) => void;
  defaultMonth?: Date;
  isCalendarLoading: boolean;
  /** The calendar icon before the date; on by default. */
  showIcon?: boolean;
}

export const BottomSheetDatePicker = ({
  availableDates,
  onChange,
  onMonthChange,
  onOpenChange,
  open = false,
  value,
  defaultMonth,
  isCalendarLoading,
  showIcon = true,
}: BottomSheetDatePickerProps) => {
  const { locale } = useGlobalDateLocale();
  const selectedDate = new Date(`${value}T00:00:00`);
  const { t } = useTranslation();

  const handleChange = (value: Date) => {
    onOpenChange?.(false);
    onChange?.(value);
  };

  const isDayDisabled = (date: Date) => {
    return (
      !availableDates.includes(date.toISOString().split('T')[0]) ||
      isCalendarLoading
    );
  };

  return (
    <>
      <SelectTriggerButton
        aria-label={t('selectDate')}
        leftIcon={
          showIcon ? (
            <IconSpan>
              <FilledCalendarIcon />
            </IconSpan>
          ) : undefined
        }
        onClick={() => onOpenChange?.(true)}
      >
        <SessionDateSpan>{formatEventMonthDay(value, locale)}</SessionDateSpan>
      </SelectTriggerButton>
      <BottomDrawer
        size="full"
        open={open}
        heading="Date"
        onClose={() => onOpenChange?.(false)}
      >
        <MobileDatePickerWrapper>
          <DayPicker
            className="bottom-drawer-calendar-availability-wrapper"
            mode="single"
            animate
            required
            numberOfMonths={1}
            selected={selectedDate}
            onSelect={handleChange}
            modifiers={{
              disabled: (date) => isDayDisabled(date),
            }}
            onNextClick={(date) => onMonthChange(moment(date))}
            onPrevClick={(date) => onMonthChange(moment(date))}
            defaultMonth={defaultMonth ?? selectedDate ?? new Date()}
            isLoading={isCalendarLoading}
            containerStyles={{
              width: '100%',
              padding: 0,
              boxShadow: 'none',
            }}
          />
        </MobileDatePickerWrapper>
      </BottomDrawer>
    </>
  );
};

const SessionDateSpan = styled.span`
  @media screen and (max-width: 395px) {
    font-size: 12px;
  }
`;

const IconSpan = styled.span`
  cursor: pointer;
  display: grid;
  place-items: center;
  width: min-content;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
`;

const MobileDatePickerWrapper = styled.div`
  width: 100%;

  & .rdp-month_grid {
    width: 100%;
  }

  & .rdp-months {
    max-width: none;
  }

  & .rdp-month {
    width: 100%;
  }

  & .rdp-day button {
    aspect-ratio: 1 / 1;
    width: 100%;
    height: auto;
    display: block;
  }
`;
